import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { match } from 'ts-pattern';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FCC,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
} from '@eluve/components';
import { useAnalytics } from '@eluve/posthog-react';
import type { CatalogPropsMap, LinkAnalytics } from '@eluve/sdui-types';

import { useSDUIAnalyticsContext } from './SDUIAnalyticsContext';

type LinkAction = CatalogPropsMap['Link']['action'];

/** Dialog state management — keeps popover open when a nested dialog is active */
interface DialogStateContextValue {
  onDialogOpenChange: (isOpen: boolean) => void;
}

const DialogStateContext = createContext<DialogStateContextValue | null>(null);

const useDialogState = () => {
  const context = useContext(DialogStateContext);
  return context;
};

/** Small numbered circle used for inline citation indicators */
const CitationBadge: FC<{ number: number }> = ({ number }) => (
  <span className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-bg-gray-subtle text-[10px] leading-none font-semibold text-text-primary">
    {number}
  </span>
);

/** Inline trigger content — renders CitationBadge or text based on style */
const TriggerContent: FC<{
  style: string;
  citationNumber?: number;
  text: string;
}> = ({ style, citationNumber, text }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment
  style === 'citation' && citationNumber != null ? (
    <CitationBadge number={citationNumber} />
  ) : (
    <>{text}</>
  );

/** Shared dialog body with header and scrollable content */
const LinkDialogBody: FCC<{
  title: string;
  subtitle?: string;
}> = ({ title, subtitle, children }) => (
  <DialogContent size="2xl" className="rounded-lg">
    <DialogHeader className="text-left">
      <DialogTitle>{title}</DialogTitle>
      {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
    </DialogHeader>
    <Stack
      onWheel={(e) => e.stopPropagation()} // Prevent Radix scroll lock interference
      align="stretch"
      className="max-h-[80vh] overflow-y-auto"
      gap={2}
    >
      {children}
    </Stack>
  </DialogContent>
);

interface HoverPopoverProps {
  text: string;
  style: string;
  citationNumber?: number;
  asSpan: boolean;
  linkClassName: string;
  wrapperClassName: string;
  children?: ReactNode;
  onTriggerClick: () => void;
}

/**
 * A popover that opens on hover and stays open when hovering over content.
 * Uses controlled state to allow interaction with content (e.g., clicking links).
 */
const HoverPopover: FC<HoverPopoverProps> = ({
  text,
  style,
  citationNumber,
  asSpan,
  linkClassName,
  wrapperClassName,
  children,
  onTriggerClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Don't close if a dialog is open
    if (isDialogOpen) {
      return;
    }
    // Small delay before closing to allow moving to popover content
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  }, [isDialogOpen]);

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setIsDialogOpen(open);
    // If dialog opens, keep popover open
    if (open) {
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      // Ensure popover stays open
      setIsOpen(true);
    }
  }, []);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      // Don't allow popover to close if a dialog is open
      if (!open && isDialogOpen) {
        return;
      }
      setIsOpen(open);
    },
    [isDialogOpen],
  );

  const TriggerElement = asSpan ? 'span' : 'button';
  const triggerProps = asSpan
    ? { role: 'button' as const, tabIndex: 0 }
    : { type: 'button' as const };

  const dialogStateContextValue = useMemo(
    () => ({ onDialogOpenChange: handleDialogOpenChange }),
    [handleDialogOpenChange],
  );

  return (
    <DialogStateContext.Provider value={dialogStateContextValue}>
      <span className={wrapperClassName}>
        <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
          <PopoverTrigger asChild>
            {style === 'button' && !asSpan ? (
              <span
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <Button variant="outline" size="sm" onClick={onTriggerClick}>
                  {text}
                </Button>
              </span>
            ) : (
              <TriggerElement
                {...triggerProps}
                className={linkClassName}
                onClick={onTriggerClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <TriggerContent
                  style={style}
                  citationNumber={citationNumber}
                  text={text}
                />
              </TriggerElement>
            )}
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="start"
            className="w-auto max-w-sm"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {style === 'citation' && citationNumber != null ? (
              <Stack direction="horizontal" gap={2} align="stretch">
                <CitationBadge number={citationNumber} />
                <Stack align="stretch" gap={1}>
                  {children}
                </Stack>
              </Stack>
            ) : (
              <Stack align="stretch" gap={2}>
                {children}
              </Stack>
            )}
          </PopoverContent>
        </Popover>
      </span>
    </DialogStateContext.Provider>
  );
};

export interface LinkRendererProps {
  text: string;
  style?: 'citation' | 'citation-source' | 'inline' | 'button' | 'source';
  citationNumber?: number;
  action: LinkAction;
  analytics?: LinkAnalytics;
  asSpan?: boolean;
  children?: ReactNode;
}

interface DialogLinkProps {
  dialogAction: {
    type: 'dialog';
    title: string;
    subtitle?: string;
  };
  text: string;
  style: string;
  citationNumber?: number;
  asSpan: boolean;
  linkClassName: string;
  wrapperClassName: string;
  children?: ReactNode;
  onTriggerClick: () => void;
}

const DialogLink: FC<DialogLinkProps> = ({
  dialogAction,
  text,
  style,
  citationNumber,
  asSpan,
  linkClassName,
  wrapperClassName,
  children,
  onTriggerClick,
}) => {
  const dialogState = useDialogState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (dialogState) {
      dialogState.onDialogOpenChange(isDialogOpen);
    }
  }, [isDialogOpen, dialogState]);

  useEffect(() => {
    // Cleanup: notify parent that dialog is closed when unmounting
    return () => {
      if (dialogState) {
        dialogState.onDialogOpenChange(false);
      }
    };
  }, [dialogState]);

  const TriggerElement = asSpan ? 'span' : 'button';
  const triggerProps = asSpan
    ? { role: 'button' as const, tabIndex: 0 }
    : { type: 'button' as const };

  // Special rendering for source style with citation number
  if (style === 'source' && citationNumber != null) {
    return (
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <Stack direction="horizontal" align="baseline" gap={2}>
          <Text>{citationNumber}.</Text>
          <DialogTrigger asChild>
            <TriggerElement
              {...triggerProps}
              className={linkClassName}
              onClick={onTriggerClick}
            >
              {text}
            </TriggerElement>
          </DialogTrigger>
          {dialogAction.subtitle && (
            <Text weight="regular" color="tertiary">
              {dialogAction.subtitle}
            </Text>
          )}
        </Stack>
        <LinkDialogBody
          title={dialogAction.title}
          subtitle={dialogAction.subtitle}
        >
          {children}
        </LinkDialogBody>
      </Dialog>
    );
  }

  return (
    <span className={wrapperClassName}>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          {style === 'button' && !asSpan ? (
            <Button variant="outline" size="sm" onClick={onTriggerClick}>
              {text}
            </Button>
          ) : (
            <TriggerElement
              {...triggerProps}
              className={linkClassName}
              onClick={onTriggerClick}
            >
              <TriggerContent
                style={style}
                citationNumber={citationNumber}
                text={text}
              />
            </TriggerElement>
          )}
        </DialogTrigger>
        <LinkDialogBody
          title={dialogAction.title}
          subtitle={dialogAction.subtitle}
        >
          {children}
        </LinkDialogBody>
      </Dialog>
    </span>
  );
};

export const LinkRenderer: FC<LinkRendererProps> = ({
  text,
  style = 'inline',
  citationNumber,
  action,
  analytics,
  asSpan = false,
  children,
}) => {
  const { capture } = useAnalytics();
  const sduiAnalytics = useSDUIAnalyticsContext();

  const handleClick = useCallback(() => {
    sduiAnalytics?.onLinkClicked?.();
    if (analytics?.onClick) {
      capture(analytics.onClick.event, {
        ...analytics.onClick.properties,
        appointmentId: sduiAnalytics?.appointmentId,
        view: sduiAnalytics?.view,
        time_since_loaded_ms: sduiAnalytics?.getTimeSinceLoaded?.() ?? null,
      });
    }
  }, [analytics, capture, sduiAnalytics]);

  const linkClassName = match(style)
    .with('citation', () => 'inline-flex items-center cursor-pointer')
    .with(
      'citation-source',
      () =>
        'text-sm font-semibold cursor-pointer text-text-primary underline hover:no-underline',
    )
    .with(
      'inline',
      () => 'text-xs font-medium cursor-pointer text-text-info hover:underline',
    )
    .with(
      'source',
      () =>
        'text-sm font-medium cursor-pointer text-text-primary hover:underline',
    )
    .with('button', () => '')
    .otherwise(
      () => 'text-xs font-medium cursor-pointer text-text-info hover:underline',
    );

  const wrapperClassName = match(style)
    .with('citation', () => 'inline-flex items-center text-left')
    .otherwise(() => 'inline-block text-left');

  const TriggerElement = asSpan ? 'span' : 'button';
  const triggerProps = asSpan
    ? { role: 'button' as const, tabIndex: 0 }
    : { type: 'button' as const };

  return match(action)
    .with({ type: 'popover' }, (popoverAction) => {
      if (popoverAction.trigger === 'hover') {
        return (
          <HoverPopover
            text={text}
            style={style}
            citationNumber={citationNumber}
            asSpan={asSpan}
            linkClassName={linkClassName}
            wrapperClassName={wrapperClassName}
            onTriggerClick={handleClick}
          >
            {children}
          </HoverPopover>
        );
      }

      return (
        <span className={wrapperClassName}>
          <Popover>
            <PopoverTrigger asChild>
              {style === 'button' && !asSpan ? (
                <Button variant="outline" size="sm" onClick={handleClick}>
                  {text}
                </Button>
              ) : (
                <TriggerElement
                  {...triggerProps}
                  className={linkClassName}
                  onClick={handleClick}
                >
                  <TriggerContent
                    style={style}
                    citationNumber={citationNumber}
                    text={text}
                  />
                </TriggerElement>
              )}
            </PopoverTrigger>
            <PopoverContent
              side="top"
              align="start"
              className="w-auto max-w-sm"
            >
              <Stack align="stretch" gap={2}>
                {children}
              </Stack>
            </PopoverContent>
          </Popover>
        </span>
      );
    })
    .with({ type: 'dialog' }, (dialogAction) => (
      <DialogLink
        dialogAction={dialogAction}
        text={text}
        style={style}
        citationNumber={citationNumber}
        asSpan={asSpan}
        linkClassName={linkClassName}
        wrapperClassName={wrapperClassName}
        onTriggerClick={handleClick}
      >
        {children}
      </DialogLink>
    ))
    .with({ type: 'navigate' }, (navigateAction) => (
      <span className={wrapperClassName}>
        {style === 'button' ? (
          <a
            href={navigateAction.url}
            target={navigateAction.target ?? '_blank'}
            rel="noopener noreferrer"
            onClick={handleClick}
          >
            <Button variant="outline" size="sm">
              {text}
            </Button>
          </a>
        ) : (
          <a
            href={navigateAction.url}
            target={navigateAction.target ?? '_blank'}
            rel="noopener noreferrer"
            className={linkClassName}
            onClick={handleClick}
          >
            {text}
          </a>
        )}
      </span>
    ))
    .otherwise(() => (
      <span className={wrapperClassName}>
        <span className={linkClassName}>{text}</span>
      </span>
    ));
};
